import React from 'react';
import { useState } from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { SettingsCoordinator } from '~settings-coordinator/SettingsCoordinator';
import { RemoteRefreshAgents } from '~src/extractRefresh/resources/RemoteRefreshAgents';
import {
  BridgeSiteSettings,
  BridgeSiteSettingsProps,
  SiteBridgeSettingsForSiteAdminProps
} from '~src/settings/bridge-settings/BridgeSiteSettings';
import { IGetEdgePoolsResult } from '~src/settings/bridge-settings/serverApi/requests/GetEdgePoolsRequest';
import { IRemoteAgentConnectionStatus } from '~src/settings/bridge-settings/serverApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { EdgePool } from '~src/settings/bridge-settings/serverApi/resources/EdgePool';

import { EdgePoolsPayload } from './sections/types/EdgePoolsPayload';
import { getErrorMessage } from './sections/utils/EdgePoolErrorUtilities';

const initialRemoteAgentConnectionStatuses: IRemoteAgentConnectionStatus[] = [];
const initialEdgePools: EdgePoolsPayload = {
  userDefinedPools: {},
  defaultPoolAgents: {},
  unassignedAgents: {},
  blockedDomains: {},
  dataConnectDomains: {},
  siteId: ''
};

/**
 * Creates a settings coordinator with the site bridge settings
 */
export function BridgeSiteSettingsCoordinator(props: SiteBridgeSettingsForSiteAdminProps) {
  const [remoteAgentConnectionStatuses, setRemoteAgentConnectionStatuses] = useState(initialRemoteAgentConnectionStatuses);
  const [edgePools, setEdgePools] = useState(initialEdgePools);
  const [hasLoadedEdgePools, setHasLoadedEdgePools] = useState(false);

  React.useEffect(() => {
    RemoteRefreshAgents.getSiteRemoteAgentsConnectionStatus()
      .then((result: IRemoteAgentConnectionStatus[]) => {
        setRemoteAgentConnectionStatuses(result);
      });
  }, []);

  React.useEffect(() => {
    FeatureFlags.edgeMultiPoolEnabled && EdgePool.getPools({ siteId: props.settings.id })
      .then((result: IGetEdgePoolsResult) => {
        if (result.success) {
          setEdgePools(result.success);
          setHasLoadedEdgePools(true);
        }
        if (result.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getErrorMessage(result.error.errorCode, {poolName: null, domainName: null, agentName: null})
          });
        }
      });
  }, [props.settings.id]);

  const refreshEdgePools = React.useCallback(() => {
    FeatureFlags.edgeMultiPoolEnabled && EdgePool.getPools({ siteId: props.settings.id })
      .then((result: IGetEdgePoolsResult) => {
        if (result.success) {
          setEdgePools(result.success);
        }
        if (result.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getErrorMessage(result.error.errorCode, {poolName: null, domainName: null, agentName: null})
          });
        }
      });
  }, [props.settings.id]);

  const newProps: BridgeSiteSettingsProps = {
    settings: props.settings,
    connectionStatuses: remoteAgentConnectionStatuses,
    edgePools: edgePools,
    refreshEdgePools,
    hasLoadedEdgePools
  };

  return (
    <SettingsCoordinator allowContentOverflow={true}>
      <BridgeSiteSettings {...newProps} />
    </SettingsCoordinator>
  );
}
